import React from 'react'
import { Refine, Authenticated } from '@refinedev/core'
import type { AuthProvider } from '@refinedev/core'
import { dataProvider, liveProvider } from '@refinedev/supabase'
import {
  useNotificationProvider,
  ErrorComponent,
} from '@refinedev/antd'
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router'
import routerBindings, {
  UnsavedChangesNotifier,
  DocumentTitleHandler,
  CatchAllNavigate,
  NavigateToResource,
} from '@refinedev/react-router'
import { App as AntdApp, ConfigProvider, Spin } from 'antd'
import '@refinedev/antd/dist/reset.css'
import { adminTheme } from './theme/antdTheme'
import { AdminLayout } from './components/AdminLayout'
import { Dashboard } from './pages/Dashboard'
import { Login } from './pages/Login'

import { supabaseClient } from './lib/supabaseClient'
import { getActiveTenant, brandDocumentTitle } from './lib/tenants'
import { ProfileList } from './resources/profiles/list'
import { ProfileCreate } from './resources/profiles/create'
import { ProfileEdit } from './resources/profiles/edit'
import { ProfileShow } from './resources/profiles/show'
import { EventList } from './resources/events/list'
import { EventCreate } from './resources/events/create'
import { EventEdit } from './resources/events/edit'
import { EventShow } from './resources/events/show'
import { EventSessionList } from './resources/event_sessions/list'
import { EventSessionCreate } from './resources/event_sessions/create'
import { EventSessionEdit } from './resources/event_sessions/edit'
import { EventSessionShow } from './resources/event_sessions/show'
import { RsvpList } from './resources/rsvps/list'
import { RsvpShow } from './resources/rsvps/show'
import { RsvpEdit } from './resources/rsvps/edit'
import { MealList } from './resources/meals/list'
import { MealCreate } from './resources/meals/create'
import { MealEdit } from './resources/meals/edit'
import { CampaignList } from './resources/campaigns/list'
import { CampaignCreate } from './resources/campaigns/create'
import { CampaignEdit } from './resources/campaigns/edit'
import { CampaignShow } from './resources/campaigns/show'
import { DonationList } from './resources/donations/list'
import { DonationShow } from './resources/donations/show'
import { DonationEdit } from './resources/donations/edit'
import { FundList } from './resources/funds/list'
import { FundCreate } from './resources/funds/create'
import { FundEdit } from './resources/funds/edit'
import { HonorYearList } from './resources/honor_years/list'
import { HonorYearCreate } from './resources/honor_years/create'
import { HonorYearEdit } from './resources/honor_years/edit'
import { ClassStandingsList } from './resources/class_standings/list'
import { ArticleList } from './resources/articles/list'
import { ArticleCreate } from './resources/articles/create'
import { ArticleEdit } from './resources/articles/edit'
import { ArticleShow } from './resources/articles/show'
import { ReleaseList } from './resources/releases/list'
import { ReleaseCreate } from './resources/releases/create'
import { ReleaseEdit } from './resources/releases/edit'
import { ReleaseShow } from './resources/releases/show'
import { NotificationList } from './resources/notifications/list'
import { NotificationCreate } from './resources/notifications/create'
import { NotificationShow } from './resources/notifications/show'

// Phase 9 — Community
import { StreamCommunityInfo } from './resources/stream_community/info'
import { ClassChatsPage } from './resources/class_chats/page'
import { MentorshipList } from './resources/mentorships/list'
import { MentorshipEdit } from './resources/mentorships/edit'
import { MentorshipShow } from './resources/mentorships/show'
import { MentorshipRequestEdit } from './resources/mentorship_requests/edit'
import { MentorshipRequestShow } from './resources/mentorship_requests/show'
import { MentorshipRelationshipList } from './resources/mentorship_relationships/list'
import { MentorshipRelationshipEdit } from './resources/mentorship_relationships/edit'
import { MentorshipRelationshipShow } from './resources/mentorship_relationships/show'
import { JobList } from './resources/jobs/list'
import { JobCreate } from './resources/jobs/create'
import { JobEdit } from './resources/jobs/edit'
import { JobShow } from './resources/jobs/show'
import { SuggestionList } from './resources/suggestions/list'
import { SuggestionShow } from './resources/suggestions/show'
import { SuggestionEdit } from './resources/suggestions/edit'
import { GroupsList } from './resources/groups/list'
import { GroupEdit } from './resources/groups/edit'
import { GroupShow } from './resources/groups/show'
import { GroupProposalsList } from './resources/group_proposals/list'
import { GroupIdeasList } from './resources/group_ideas/list'
import { GroupIdeaShow } from './resources/group_ideas/show'
import { GroupChaptersList } from './resources/group_chapters/list'
import { GroupsSettingsPage } from './resources/groups_settings/page'
import { GroupMeetupList } from './resources/group_meetups/list'
import { GroupMeetupShow } from './resources/group_meetups/show'
import { GroupCallsList } from './resources/group_calls/list'
import { GroupsInsightsPage } from './resources/group_insights/page'

// Engagement loops — read-only data views
import { MemberBadgesList } from './resources/member_badges/list'
import { DevotionalReadsList } from './resources/devotional_reads/list'

// Phase 9 — Content (devotionals, albums, photos)
import { DevotionalList } from './resources/devotionals/list'
import { DevotionalCreate } from './resources/devotionals/create'
import { DevotionalEdit } from './resources/devotionals/edit'
import { DevotionalShow } from './resources/devotionals/show'
import { AlbumList } from './resources/albums/list'
import { AlbumCreate } from './resources/albums/create'
import { AlbumEdit } from './resources/albums/edit'
import { AlbumShow } from './resources/albums/show'
import { PhotoList } from './resources/photos/list'
import { PhotoCreate } from './resources/photos/create'
import { PhotoEdit } from './resources/photos/edit'
import { PhotoShow } from './resources/photos/show'
import { PhotoTagList } from './resources/photo_tags/list'

// Phase 9 — System
import { UserSettingList } from './resources/user_settings/list'
import { UserSettingShow } from './resources/user_settings/show'

// Admin build-out — real-time control, push, activity, integrity
import { FeatureFlagList } from './resources/feature_flags/list'
import { FeatureFlagCreate } from './resources/feature_flags/create'
import { FeatureFlagEdit } from './resources/feature_flags/edit'
import { AdminUsersList } from './resources/admin_users/list'
import { ActivityFeedList } from './resources/activity_feed/list'
import { ModerationActionList } from './resources/moderation_actions/list'
import { UserPostList } from './resources/user_posts/list'
import { MemoryCommentsList } from './resources/memory_comments/list'
import { SendPushPage } from './resources/send_push/page'
import { PurgeTestUsersPage } from './resources/purge_test_users/page'
import { PhotoTagBackfillPage } from './resources/photo_tag_backfill/page'
import { FeedModerationPage } from './resources/feed_moderation/page'
import { NotificationRuleList } from './resources/notification_rules/list'
import { NotificationRuleCreate } from './resources/notification_rules/create'
import { NotificationRuleEdit } from './resources/notification_rules/edit'
// Code-split the three pages that pull the heavy chart/graph libs
// (@ant-design/plots, @xyflow/react + dagre). React.lazy keeps those libs out of
// the initial bundle so the cold load doesn't parse them before any page paints;
// they download only when their page is opened. (manualChunks in vite.config.ts
// puts each lib in its own cacheable chunk.)
// Runs and Signals are the two pages most likely to grow a chart, so they are
// lazy. Inbox and Controls are static imports on purpose: Inbox is the daily
// surface and must paint immediately, and Controls has to load even when
// everything else is broken — the same reasoning as KillSwitchPage.
const CmRunsPage = React.lazy(() =>
  import('./resources/community_manager/runs/page').then((m) => ({ default: m.CmRunsPage })),
)
const CmSignalsPage = React.lazy(() =>
  import('./resources/community_manager/signals/page').then((m) => ({ default: m.CmSignalsPage })),
)
const MentorshipHubPage = React.lazy(() =>
  import('./resources/mentorship_requests/page').then((m) => ({ default: m.MentorshipHubPage })),
)
const ScreensAnalyticsPage = React.lazy(() =>
  import('./resources/analytics/screens').then((m) => ({ default: m.ScreensAnalyticsPage })),
)
const FlowAnalyticsPage = React.lazy(() =>
  import('./resources/analytics/flow').then((m) => ({ default: m.FlowAnalyticsPage })),
)
const SentryIssuesPage = React.lazy(() =>
  import('./resources/sentry/page').then((m) => ({ default: m.SentryIssuesPage })),
)
const AuditLogList = React.lazy(() =>
  import('./resources/audit_log/list').then((m) => ({ default: m.AuditLogList })),
)

// Push broadcasts
import { PushBroadcastsList } from './resources/push_broadcasts/list'

// Trust & Safety
import { ReportsList } from './resources/reports/list'
import { ReportShow } from './resources/reports/show'
import { ClassYearChangeRequestsList } from './resources/class_year_change_requests/list'
import { ClassYearChangeRequestShow } from './resources/class_year_change_requests/show'
import { BusinessesList } from './resources/businesses/list'
import { BusinessShow } from './resources/businesses/show'
import { BusinessOfMonthPage } from './resources/business_spotlights/page'
import { AskMonitorPage } from './resources/business_requests/page'
import { JaguarOwnedControlsPage } from './resources/jaguar_owned_controls/page'
import { CmInboxPage } from './resources/community_manager/inbox/page'
import { CmControlsPage } from './resources/community_manager/controls/page'
import { TextBlocklistList } from './resources/text_blocklist/list'
import { TextBlocklistCreate } from './resources/text_blocklist/create'
import { TextBlocklistEdit } from './resources/text_blocklist/edit'
import { IntegritySettingsPage } from './resources/integrity_settings/page'
import { OnboardingSettingsPage } from './resources/onboarding_settings/page'
import { DemoSettingsPage } from './resources/demo_settings/page'
import { IntegrityActionsPage } from './resources/integrity_actions/page'

// Giving — payment-provider settings (Stripe / Cheddar Up)
import { GivingSettingsPage } from './resources/giving_settings/page'

// System — global app kill switch
import { KillSwitchPage } from './pages/KillSwitch'
import { ForceUpdatePage } from './pages/ForceUpdate'
import { FeedLayoutPage } from './pages/FeedLayout'
import { PhotoBulkUploadPage } from './pages/PhotoBulkUpload'

const authProvider: AuthProvider = {
  login: async ({ email, password }: { email: string; password: string }) => {
    const { data, error } = await supabaseClient.auth.signInWithPassword({
      email,
      password,
    })

    if (error || !data.session) {
      return {
        success: false,
        error: error ?? new Error('Login failed'),
      }
    }

    // Verify the user has admin role
    const { data: profile, error: profileError } = await supabaseClient
      .from('profiles')
      .select('role')
      .eq('user_id', data.session.user.id)
      .single()

    if (profileError || !profile) {
      await supabaseClient.auth.signOut()
      return {
        success: false,
        error: new Error('Could not verify admin status'),
      }
    }

    if (profile.role !== 'admin') {
      await supabaseClient.auth.signOut()
      return {
        success: false,
        error: new Error('Access denied: admin role required'),
      }
    }

    return { success: true }
  },

  logout: async () => {
    await supabaseClient.auth.signOut()
    return { success: true }
  },

  check: async () => {
    const { data } = await supabaseClient.auth.getSession()
    if (data.session) {
      return { authenticated: true }
    }
    return {
      authenticated: false,
      redirectTo: '/login',
      logout: true,
    }
  },

  getIdentity: async () => {
    const { data } = await supabaseClient.auth.getSession()
    if (!data.session?.user) return null

    const { data: profile } = await supabaseClient
      .from('profiles')
      .select('name, email, avatar_url')
      .eq('user_id', data.session.user.id)
      .single()

    return {
      id: data.session.user.id,
      name: profile?.name ?? data.session.user.email,
      email: profile?.email ?? data.session.user.email,
      avatar: profile?.avatar_url ?? undefined,
    }
  },

  onError: async (error) => {
    if (error?.status === 401 || error?.status === 403) {
      return { logout: true }
    }
    return { error }
  },
}

// useNotificationProvider() must run INSIDE <AntdApp> so Refine's success/error
// toasts bind to AntD's App context. Called outside it (antd v5), it falls back to
// the static notification API ("Static function can not consume context like
// dynamic theme") and the toast never renders — which silently hid the Feed Rules
// save failure. Keeping it in this inner component fixes error visibility app-wide.
function AppShell() {
  const notificationProvider = useNotificationProvider()

  return (
          <Refine
            routerProvider={routerBindings}
            dataProvider={dataProvider(supabaseClient)}
            liveProvider={liveProvider(supabaseClient)}
            authProvider={authProvider}
            notificationProvider={notificationProvider}
            resources={[
              {
                name: 'profiles',
                list: '/profiles',
                create: '/profiles/create',
                edit: '/profiles/edit/:id',
                show: '/profiles/show/:id',
                meta: {
                  label: 'Members',
                  parent: 'Members',
                },
              },
              {
                name: 'events',
                list: '/events',
                create: '/events/create',
                edit: '/events/edit/:id',
                show: '/events/show/:id',
                meta: {
                  label: 'Events',
                  parent: 'Events',
                },
              },
              {
                name: 'event_sessions',
                list: '/event_sessions',
                create: '/event_sessions/create',
                edit: '/event_sessions/edit/:id',
                show: '/event_sessions/show/:id',
                meta: {
                  label: 'Sub-events',
                  parent: 'Events',
                },
              },
              {
                name: 'rsvps',
                list: '/rsvps',
                edit: '/rsvps/edit/:id',
                show: '/rsvps/show/:id',
                meta: {
                  label: 'RSVPs',
                  parent: 'Events',
                },
              },
              {
                name: 'meals',
                list: '/meals',
                create: '/meals/create',
                edit: '/meals/edit/:id',
                meta: {
                  label: 'Meal Options',
                  parent: 'Events',
                },
              },
              {
                name: 'campaigns',
                list: '/campaigns',
                create: '/campaigns/create',
                edit: '/campaigns/edit/:id',
                show: '/campaigns/show/:id',
                meta: {
                  label: 'Campaigns',
                  parent: 'Giving',
                  // Admins may delete a campaign (RLS "campaigns: admin delete" has
                  // always allowed it). A campaign that has donations is still
                  // protected by the donations.campaign_id foreign key, so giving
                  // history can never be deleted along with the drive.
                  canDelete: true,
                },
              },
              {
                name: 'donations',
                list: '/donations',
                edit: '/donations/edit/:id',
                show: '/donations/show/:id',
                meta: {
                  label: 'Gifts',
                  parent: 'Giving',
                },
              },
              {
                name: 'funds',
                list: '/funds',
                create: '/funds/create',
                edit: '/funds/edit/:id',
                meta: {
                  label: 'Designations',
                  parent: 'Giving',
                },
              },
              {
                name: 'honor_years',
                list: '/honor_years',
                create: '/honor_years/create',
                edit: '/honor_years/edit/:id',
                meta: {
                  label: 'Honor Years',
                  parent: 'Giving',
                  idColumnName: 'year',
                },
              },
              {
                name: 'class_standings',
                list: '/class_standings',
                meta: {
                  label: 'Class Standings',
                  parent: 'Giving',
                  idColumnName: 'class_year',
                },
              },
              {
                name: 'giving_settings',
                list: '/giving_settings',
                meta: {
                  label: 'Giving Settings',
                  parent: 'Giving',
                  // Single-row table: the only row has boolean PK id = true.
                  idColumnName: 'id',
                },
              },
              {
                name: 'articles',
                list: '/articles',
                create: '/articles/create',
                edit: '/articles/edit/:id',
                show: '/articles/show/:id',
                meta: {
                  label: 'Articles',
                  parent: 'Content',
                },
              },
              {
                name: 'releases',
                list: '/releases',
                create: '/releases/create',
                edit: '/releases/edit/:id',
                show: '/releases/show/:id',
                meta: {
                  label: "What's New",
                  parent: 'Content',
                },
              },
              {
                name: 'devotionals',
                list: '/devotionals',
                create: '/devotionals/create',
                edit: '/devotionals/edit/:id',
                show: '/devotionals/show/:id',
                meta: {
                  label: 'Devotionals',
                  parent: 'Content',
                },
              },
              {
                name: 'albums',
                list: '/albums',
                create: '/albums/create',
                edit: '/albums/edit/:id',
                show: '/albums/show/:id',
                meta: {
                  label: 'Albums',
                  parent: 'Content',
                },
              },
              {
                name: 'photos',
                list: '/photos',
                create: '/photos/create',
                edit: '/photos/edit/:id',
                show: '/photos/show/:id',
                meta: {
                  label: 'Photos',
                  parent: 'Content',
                },
              },
              {
                name: 'photo_tags',
                list: '/photo_tags',
                meta: {
                  label: 'Tag Queue',
                  parent: 'Content',
                },
              },
              {
                // Member-tagged-member nominations. No own page — the Photo Tags
                // list unions these in via admin_photo_tags_overview; registered
                // only so that list's per-row DeleteButton can resolve the real
                // source table when a nomination row is removed. Hidden from nav
                // (the curated adminNav doesn't reference it).
                name: 'photo_tag_nominations',
                meta: {
                  label: 'Photo Tag Nominations',
                  parent: 'Content',
                  hide: true,
                },
              },
              {
                name: 'notifications',
                list: '/notifications',
                create: '/notifications/create',
                show: '/notifications/show/:id',
                meta: {
                  label: 'In-app Notifications',
                  parent: 'Messaging',
                },
              },
              {
                name: 'stream_community',
                list: '/community',
                meta: {
                  label: 'Reunion Chats',
                  parent: 'Community',
                },
              },
              {
                name: 'class_chats',
                list: '/class-chats',
                meta: { label: 'Class Chats', parent: 'Community' },
              },
              {
                // Approval queue for member-requested class-year changes. List +
                // show only: decisions run through the class-year-request-decide
                // Edge Function (profile write + class-chat move + notify + audit),
                // and the table has no admin UPDATE policy, so there is no edit route.
                name: 'class_year_change_requests',
                list: '/class_year_change_requests',
                show: '/class_year_change_requests/show/:id',
                meta: { label: 'Class Year Changes', parent: 'Members' },
              },
              {
                // Jaguar-Owned listings queue. List + show only: decisions
                // (approve / request changes / reject / remove) go through the
                // business-listing-decide Edge Function, and the table has no
                // admin UPDATE policy, so there is no edit route.
                name: 'businesses',
                list: '/businesses',
                show: '/businesses/show/:id',
                meta: { label: 'Jaguar-Owned Listings', parent: 'Community' },
              },
              {
                // Business of the Month console (D2): nominations + eligibility
                // now, scoring + scheduling land in D3. Its own custom page (like
                // giving_settings), so it needs only a list route.
                name: 'business_spotlights',
                list: '/business-of-the-month',
                meta: { label: 'Business of the Month', parent: 'Community' },
              },
              {
                // Ask the Jaguars monitor (K3): list + remove only, same shape as
                // the listings queue and class-year-change queue. The row's
                // before-update trigger only ever lets an admin set status to
                // 'removed' with a reason, so there is no edit route.
                name: 'business_requests',
                list: '/ask-monitor',
                meta: { label: 'Ask the Jaguars', parent: 'Community' },
              },
              {
                // Jaguar-Owned controls (Task C4): the curated page that owns
                // every jaguar_owned_* feature_flags row. Its own custom page
                // (like business_spotlights above), so only a list route.
                name: 'jaguar_owned_controls',
                list: '/jaguar-owned/controls',
                meta: { label: 'Jaguar-Owned Switches', parent: 'Community' },
              },
              {
                // The community manager agent. Four surfaces: the approval
                // queue, the situation report it reasons from, the run diary,
                // and the controls that decide how much it may do alone.
                name: 'cm_proposals',
                list: '/agent/inbox',
                meta: { label: 'Community Manager Inbox', parent: 'Messaging' },
              },
              {
                name: 'cm_signals_overview',
                list: '/agent/signals',
                meta: { label: 'Community Manager Signals', parent: 'Messaging' },
              },
              {
                name: 'cm_runs',
                list: '/agent/runs',
                meta: { label: 'Community Manager Runs', parent: 'Messaging' },
              },
              {
                // Singleton keyed on a boolean id, exactly like giving_settings.
                name: 'cm_settings',
                list: '/agent/controls',
                meta: { label: 'Community Manager Controls', parent: 'Messaging', idColumnName: 'id' },
              },
              {
                // Read via business_request_replies (reply-count rollup on the
                // Ask monitor). No own page — registered so the resource exists
                // for Refine's data provider, hidden from nav.
                name: 'business_request_replies',
                meta: { label: 'Ask the Jaguars Replies', parent: 'Community', hide: true },
              },
              {
                // Read/updated via ReportedContent (Task P3), which lets an
                // admin remove or restore a reported vouch from a report. No
                // own page — registered so the resource exists for Refine's
                // data provider, hidden from nav.
                name: 'business_vouches',
                meta: { label: 'Jaguar-Owned Vouches', parent: 'Community', hide: true },
              },
              {
                // Read/updated via ReportedContent (Task P3), which lets an
                // admin remove or restore a reported owner note. Notes get NO
                // browse UI anywhere else — admins see one only through a
                // report about it. No own page — registered so the resource
                // exists for Refine's data provider, hidden from nav.
                name: 'business_owner_notes',
                meta: { label: 'Jaguar-Owned Owner Notes', parent: 'Community', hide: true },
              },
              {
                // Read via botm_nominations (NominationsTab). No own page —
                // registered so the resource exists for Refine's data provider.
                name: 'botm_nominations',
                meta: { label: 'Business of the Month Nominations', parent: 'Community', hide: true },
              },
              {
                // Read via botm_scores in D3's Scoring tab. No own page yet.
                name: 'botm_scores',
                meta: { label: 'Business of the Month Scores', parent: 'Community', hide: true },
              },
              {
                // Admin-only view (business_spotlight_eligibility). No own page —
                // read via EligibilityTab.
                name: 'business_spotlight_eligibility',
                meta: { label: 'Business of the Month Eligibility', parent: 'Community', hide: true },
              },
              {
                name: 'mentorships',
                list: '/mentorships',
                edit: '/mentorships/edit/:id',
                show: '/mentorships/show/:id',
                meta: {
                  label: 'Mentor Profiles',
                  parent: 'Community',
                },
              },
              {
                name: 'mentorship_requests',
                list: '/mentorship_requests',
                edit: '/mentorship_requests/edit/:id',
                show: '/mentorship_requests/show/:id',
                meta: {
                  label: 'Mentorship Requests',
                  parent: 'Community',
                },
              },
              {
                name: 'mentorship_relationships',
                list: '/mentorship_relationships',
                edit: '/mentorship_relationships/edit/:id',
                show: '/mentorship_relationships/show/:id',
                meta: {
                  label: 'Mentorship Relationships',
                  parent: 'Community',
                },
              },
              {
                name: 'jobs',
                list: '/jobs',
                create: '/jobs/create',
                edit: '/jobs/edit/:id',
                show: '/jobs/show/:id',
                meta: {
                  label: 'Jobs',
                  parent: 'Community',
                },
              },
              {
                // Suggestion Box triage (20261013120000_suggestion_box.sql). No
                // create (members write from the app) and no delete (no admin
                // DELETE policy). Edit sends status only; the private note is
                // its own admin-only table (resources/suggestions/adminNote.ts).
                name: 'suggestions',
                list: '/suggestions',
                show: '/suggestions/show/:id',
                edit: '/suggestions/edit/:id',
                meta: {
                  label: 'Suggestions',
                  parent: 'Community',
                },
              },
              {
                name: 'groups',
                list: '/groups',
                edit: '/groups/edit/:id',
                show: '/groups/show/:id',
                meta: {
                  label: 'Groups',
                  parent: 'Community',
                },
              },
              {
                // Approval queue for member-proposed groups (`groups` where
                // status = pending_review). List only — decisions run through
                // review_group() from the row itself, no separate show route.
                name: 'group_proposals',
                list: '/group_proposals',
                meta: {
                  label: 'Group Proposals',
                  parent: 'Community',
                },
              },
              {
                // The pledge board's admin side (§P). group_ideas/group_idea_pledges.
                name: 'group_ideas',
                list: '/group_ideas',
                show: '/group_ideas/show/:id',
                meta: {
                  label: 'Group Ideas',
                  parent: 'Community',
                },
              },
              {
                // Read model: `groups` filtered to kind = 'class_chapter', shown as
                // "Classrooms". The page reads supabaseClient directly (groups,
                // classroom_settings, classroom_founders), so no CRUD routes here.
                name: 'group_chapters',
                list: '/group_chapters',
                meta: {
                  label: 'Classrooms',
                  parent: 'Community',
                },
              },
              {
                name: 'groups_settings',
                list: '/groups_settings',
                meta: {
                  label: 'Group Settings',
                  parent: 'Community',
                },
              },
              {
                name: 'group_meetups',
                list: '/group_meetups',
                show: '/group_meetups/show/:id',
                meta: {
                  label: 'Meetups',
                  parent: 'Community',
                },
              },
              {
                // Ad-hoc video huddles (start_huddle/end_huddle,
                // 20261007120000_group_calls.sql; Groups Phase 5, Task 1).
                // List only — "End call" runs from the row itself.
                name: 'group_calls',
                list: '/group_calls',
                meta: {
                  label: 'Group Calls',
                  parent: 'Community',
                },
              },
              {
                // Groups P6 "Prove" dashboard (docs/groups/GROUPS-SPEC.md §8):
                // one round trip to public.groups_insights(p_days)
                // (20261017140000_groups_insights.sql). Not a CRUD resource —
                // list-only, page reads via supabaseClient.rpc directly.
                name: 'group_insights',
                list: '/group_insights',
                meta: {
                  label: 'Group Insights',
                  parent: 'Community',
                },
              },
              {
                name: 'member_badges',
                list: '/member_badges',
                meta: { label: 'Badges', parent: 'Members' },
              },
              {
                name: 'devotional_reads',
                list: '/devotional_reads',
                meta: { label: 'Devotional Reads', parent: 'Content' },
              },
              {
                name: 'user_settings',
                list: '/user_settings',
                show: '/user_settings/show/:id',
                meta: {
                  label: 'Member Settings',
                  parent: 'Members',
                  idColumnName: 'user_id',
                },
              },
              {
                name: 'app_config',
                list: '/kill-switch',
                meta: {
                  label: 'Kill Switch & Notices',
                  parent: 'System',
                  // Single-row table: the only row has boolean PK id = true.
                  idColumnName: 'id',
                },
              },
              {
                // Reads live errors from Sentry via the admin-gated sentry-proxy
                // Edge Function — not a DB table.
                name: 'sentry_issues',
                list: '/sentry',
                meta: { label: 'App Health', parent: 'Overview' },
              },
              {
                name: 'admin_users',
                list: '/admin_users',
                meta: { label: 'Accounts & Admins', parent: 'Members', idColumnName: 'user_id' },
              },
              {
                name: 'activity_feed',
                list: '/activity_feed',
                meta: { label: 'Activity', parent: 'Overview' },
              },
              {
                name: 'send_push',
                list: '/send-push',
                meta: { label: 'Send Push', parent: 'Messaging' },
              },
              {
                name: 'purge_test_users',
                list: '/purge-test-users',
                meta: { label: 'Test Data', parent: 'System' },
              },
              {
                name: 'photo_tag_backfill',
                list: '/photo-tag-backfill',
                meta: { label: 'Photo Tag Backfill', parent: 'System' },
              },
              {
                name: 'feed_moderation',
                list: '/feed-moderation',
                meta: { label: 'Feed Moderation', parent: 'Trust & Safety' },
              },
              {
                name: 'push_broadcasts',
                list: '/push-broadcasts',
                meta: { label: 'Sent History', parent: 'Messaging' },
              },
              {
                name: 'feature_flags',
                list: '/feature_flags',
                create: '/feature_flags/create',
                edit: '/feature_flags/edit/:id',
                meta: { label: 'Feature Flags', parent: 'System', idColumnName: 'key' },
              },
              {
                name: 'onboarding_settings',
                list: '/onboarding_settings',
                meta: { label: 'Onboarding', parent: 'System' },
              },
              {
                name: 'demo_settings',
                list: '/demo_settings',
                meta: { label: 'Demo Mode', parent: 'System' },
              },
              {
                name: 'moderation_actions',
                list: '/moderation_actions',
                meta: { label: 'Moderation Log', parent: 'Trust & Safety' },
              },
              {
                name: 'admin_audit_log',
                list: '/audit_log',
                meta: { label: 'Audit Trail', parent: 'System', idColumnName: 'seq' },
              },
              {
                name: 'user_posts',
                list: '/user_posts',
                meta: { label: 'Member Posts', parent: 'Trust & Safety' },
              },
              {
                // Classroom memory comments (Classrooms Phase 1). Read-and-delete
                // only: the table has no update policy. A tab of Feed Moderation.
                name: 'memory_comments',
                list: '/memory_comments',
                meta: { label: 'Memory Comments', parent: 'Trust & Safety' },
              },
              {
                name: 'notification_rules',
                list: '/notification_rules',
                create: '/notification_rules/create',
                edit: '/notification_rules/edit/:id',
                meta: { label: 'Notification Rules', parent: 'Messaging' },
              },
              {
                name: 'integrity_settings',
                list: '/integrity_settings',
                meta: { label: 'Safety Controls', parent: 'Trust & Safety' },
              },
              {
                name: 'integrity_actions',
                list: '/integrity-actions',
                meta: { label: 'Integrity Actions', parent: 'Trust & Safety' },
              },
              {
                name: 'reports',
                list: '/reports',
                show: '/reports/show/:id',
                meta: { label: 'Reports Queue', parent: 'Trust & Safety' },
              },
              {
                name: 'text_blocklist',
                list: '/text_blocklist',
                create: '/text_blocklist/create',
                edit: '/text_blocklist/edit/:id',
                meta: { label: 'Text Blocklist', parent: 'Trust & Safety', idColumnName: 'term' },
              },
              {
                // name matches the DB view the page reads (analytics_screen_stats).
                name: 'analytics_screen_stats',
                list: '/analytics/screens',
                meta: { label: 'Screens', parent: 'Overview' },
              },
              {
                // name matches the DB view the page reads (analytics_screen_flow).
                name: 'analytics_screen_flow',
                list: '/analytics/flow',
                meta: { label: 'User Flow', parent: 'Overview' },
              },
            ]}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              // Breadcrumbs live in the AdminLayout header (AdminBreadcrumb),
              // one consistent branded trail on every page — so the plain AntD
              // breadcrumb inside Show/Edit/Create cards is disabled globally.
              breadcrumb: false,
              // Realtime is OPT-IN, not global. liveMode:'auto' previously opened a
              // Supabase postgres_changes channel per data hook on every page and
              // refetched (with staleTime 0, no debounce) on every write — re-running
              // each page's data fetch + aggregation and freezing the UI under load.
              // Admin pages don't need live updates; the few that do (e.g. the
              // Dashboard RSVP KPI) opt back in per-hook with liveMode:'auto'.
              liveMode: 'off',
              // Cache list/aggregate reads for 5 min so navigating back to a page or
              // re-rendering doesn't refire every query. refetchOnWindowFocus off so
              // tabbing back in doesn't stampede the API.
              reactQuery: {
                clientConfig: {
                  defaultOptions: {
                    queries: { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false },
                  },
                },
              },
            }}
          >
            <Routes>
              <Route
                element={
                  <Authenticated
                    key="authenticated-routes"
                    fallback={<CatchAllNavigate to="/login" />}
                  >
                    <AdminLayout>
                      {/* Suspense boundary for the code-split (React.lazy) pages. */}
                      <React.Suspense
                        fallback={
                          <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
                            <Spin />
                          </div>
                        }
                      >
                        <Outlet />
                      </React.Suspense>
                    </AdminLayout>
                  </Authenticated>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="/profiles">
                  <Route index element={<ProfileList />} />
                  <Route path="create" element={<ProfileCreate />} />
                  <Route path="edit/:id" element={<ProfileEdit />} />
                  <Route path="show/:id" element={<ProfileShow />} />
                </Route>
                <Route path="/events">
                  <Route index element={<EventList />} />
                  <Route path="create" element={<EventCreate />} />
                  <Route path="edit/:id" element={<EventEdit />} />
                  <Route path="show/:id" element={<EventShow />} />
                </Route>
                <Route path="/event_sessions">
                  <Route index element={<EventSessionList />} />
                  <Route path="create" element={<EventSessionCreate />} />
                  <Route path="edit/:id" element={<EventSessionEdit />} />
                  <Route path="show/:id" element={<EventSessionShow />} />
                </Route>
                <Route path="/rsvps">
                  <Route index element={<RsvpList />} />
                  <Route path="edit/:id" element={<RsvpEdit />} />
                  <Route path="show/:id" element={<RsvpShow />} />
                </Route>
                <Route path="/meals">
                  <Route index element={<MealList />} />
                  <Route path="create" element={<MealCreate />} />
                  <Route path="edit/:id" element={<MealEdit />} />
                </Route>
                <Route path="/campaigns">
                  <Route index element={<CampaignList />} />
                  <Route path="create" element={<CampaignCreate />} />
                  <Route path="edit/:id" element={<CampaignEdit />} />
                  <Route path="show/:id" element={<CampaignShow />} />
                </Route>
                <Route path="/donations">
                  <Route index element={<DonationList />} />
                  <Route path="edit/:id" element={<DonationEdit />} />
                  <Route path="show/:id" element={<DonationShow />} />
                </Route>
                <Route path="/funds">
                  <Route index element={<FundList />} />
                  <Route path="create" element={<FundCreate />} />
                  <Route path="edit/:id" element={<FundEdit />} />
                </Route>
                <Route path="/honor_years">
                  <Route index element={<HonorYearList />} />
                  <Route path="create" element={<HonorYearCreate />} />
                  <Route path="edit/:id" element={<HonorYearEdit />} />
                </Route>
                <Route path="/class_standings">
                  <Route index element={<ClassStandingsList />} />
                </Route>
                <Route path="/giving_settings" element={<GivingSettingsPage />} />
                <Route path="/articles">
                  <Route index element={<ArticleList />} />
                  <Route path="create" element={<ArticleCreate />} />
                  <Route path="edit/:id" element={<ArticleEdit />} />
                  <Route path="show/:id" element={<ArticleShow />} />
                </Route>
                <Route path="/releases">
                  <Route index element={<ReleaseList />} />
                  <Route path="create" element={<ReleaseCreate />} />
                  <Route path="edit/:id" element={<ReleaseEdit />} />
                  <Route path="show/:id" element={<ReleaseShow />} />
                </Route>
                <Route path="/notifications">
                  <Route index element={<NotificationList />} />
                  <Route path="create" element={<NotificationCreate />} />
                  <Route path="show/:id" element={<NotificationShow />} />
                </Route>
                {/* Phase 9 — Community */}
                <Route path="/community">
                  <Route index element={<StreamCommunityInfo />} />
                </Route>
                <Route path="/class-chats" element={<ClassChatsPage />} />
                <Route path="/mentorships">
                  <Route index element={<MentorshipList />} />
                  <Route path="edit/:id" element={<MentorshipEdit />} />
                  <Route path="show/:id" element={<MentorshipShow />} />
                </Route>
                <Route path="/mentorship_requests">
                  <Route index element={<MentorshipHubPage />} />
                  <Route path="edit/:id" element={<MentorshipRequestEdit />} />
                  <Route path="show/:id" element={<MentorshipRequestShow />} />
                </Route>
                <Route path="/mentorship_relationships">
                  <Route index element={<MentorshipRelationshipList />} />
                  <Route path="edit/:id" element={<MentorshipRelationshipEdit />} />
                  <Route path="show/:id" element={<MentorshipRelationshipShow />} />
                </Route>
                <Route path="/jobs">
                  <Route index element={<JobList />} />
                  <Route path="create" element={<JobCreate />} />
                  <Route path="edit/:id" element={<JobEdit />} />
                  <Route path="show/:id" element={<JobShow />} />
                </Route>
                <Route path="/suggestions">
                  <Route index element={<SuggestionList />} />
                  <Route path="show/:id" element={<SuggestionShow />} />
                  <Route path="edit/:id" element={<SuggestionEdit />} />
                </Route>
                <Route path="/groups">
                  <Route index element={<GroupsList />} />
                  <Route path="edit/:id" element={<GroupEdit />} />
                  <Route path="show/:id" element={<GroupShow />} />
                </Route>
                <Route path="/group_proposals">
                  <Route index element={<GroupProposalsList />} />
                </Route>
                <Route path="/group_ideas">
                  <Route index element={<GroupIdeasList />} />
                  <Route path="show/:id" element={<GroupIdeaShow />} />
                </Route>
                <Route path="/group_chapters">
                  <Route index element={<GroupChaptersList />} />
                </Route>
                <Route path="/groups_settings">
                  <Route index element={<GroupsSettingsPage />} />
                </Route>
                <Route path="/group_meetups">
                  <Route index element={<GroupMeetupList />} />
                  <Route path="show/:id" element={<GroupMeetupShow />} />
                </Route>
                <Route path="/group_calls">
                  <Route index element={<GroupCallsList />} />
                </Route>
                <Route path="/group_insights">
                  <Route index element={<GroupsInsightsPage />} />
                </Route>
                <Route path="/member_badges">
                  <Route index element={<MemberBadgesList />} />
                </Route>
                <Route path="/devotional_reads">
                  <Route index element={<DevotionalReadsList />} />
                </Route>

                {/* Phase 9 — Content */}
                <Route path="/devotionals">
                  <Route index element={<DevotionalList />} />
                  <Route path="create" element={<DevotionalCreate />} />
                  <Route path="edit/:id" element={<DevotionalEdit />} />
                  <Route path="show/:id" element={<DevotionalShow />} />
                </Route>
                <Route path="/albums">
                  <Route index element={<AlbumList />} />
                  <Route path="create" element={<AlbumCreate />} />
                  <Route path="edit/:id" element={<AlbumEdit />} />
                  <Route path="show/:id" element={<AlbumShow />} />
                </Route>
                <Route path="/photos">
                  <Route index element={<PhotoList />} />
                  <Route path="bulk-upload" element={<PhotoBulkUploadPage />} />
                  <Route path="create" element={<PhotoCreate />} />
                  <Route path="edit/:id" element={<PhotoEdit />} />
                  <Route path="show/:id" element={<PhotoShow />} />
                </Route>
                <Route path="/photo_tags">
                  <Route index element={<PhotoTagList />} />
                </Route>

                {/* Phase 9 — System */}
                <Route path="/user_settings">
                  <Route index element={<UserSettingList />} />
                  <Route path="show/:id" element={<UserSettingShow />} />
                </Route>
                <Route path="/kill-switch" element={<KillSwitchPage />} />
                <Route path="/force-update" element={<ForceUpdatePage />} />
                <Route path="/feed-layout" element={<FeedLayoutPage />} />

                {/* Admin build-out — real-time control, push, activity, integrity */}
                <Route path="/admin_users">
                  <Route index element={<AdminUsersList />} />
                </Route>
                <Route path="/activity_feed">
                  <Route index element={<ActivityFeedList />} />
                </Route>
                <Route path="/send-push" element={<SendPushPage />} />
                <Route path="/purge-test-users" element={<PurgeTestUsersPage />} />
                <Route path="/photo-tag-backfill" element={<PhotoTagBackfillPage />} />
                <Route path="/feed-moderation" element={<FeedModerationPage />} />
                <Route path="/push-broadcasts">
                  <Route index element={<PushBroadcastsList />} />
                </Route>
                <Route path="/feature_flags">
                  <Route index element={<FeatureFlagList />} />
                  <Route path="create" element={<FeatureFlagCreate />} />
                  <Route path="edit/:id" element={<FeatureFlagEdit />} />
                </Route>
                <Route path="/onboarding_settings">
                  <Route index element={<OnboardingSettingsPage />} />
                </Route>
                <Route path="/demo_settings">
                  <Route index element={<DemoSettingsPage />} />
                </Route>
                <Route path="/moderation_actions">
                  <Route index element={<ModerationActionList />} />
                </Route>
                <Route path="/user_posts">
                  <Route index element={<UserPostList />} />
                </Route>
                <Route path="/memory_comments">
                  <Route index element={<MemoryCommentsList />} />
                </Route>
                <Route path="/notification_rules">
                  <Route index element={<NotificationRuleList />} />
                  <Route path="create" element={<NotificationRuleCreate />} />
                  <Route path="edit/:id" element={<NotificationRuleEdit />} />
                </Route>
                <Route path="/analytics/screens" element={<ScreensAnalyticsPage />} />
                <Route path="/analytics/flow" element={<FlowAnalyticsPage />} />
                <Route path="/sentry" element={<SentryIssuesPage />} />
                <Route path="/audit_log" element={<AuditLogList />} />
                {/* Merged into the Mentorship hub — redirect old links to the map tab. */}
                <Route
                  path="/mentor-relationships"
                  element={<Navigate to="/mentorship_requests?tab=map" replace />}
                />

                {/* Trust & Safety */}
                <Route path="/integrity_settings">
                  <Route index element={<IntegritySettingsPage />} />
                </Route>
                <Route path="/integrity-actions" element={<IntegrityActionsPage />} />
                <Route path="/reports">
                  <Route index element={<ReportsList />} />
                  <Route path="show/:id" element={<ReportShow />} />
                </Route>
                <Route path="/class_year_change_requests">
                  <Route index element={<ClassYearChangeRequestsList />} />
                  <Route path="show/:id" element={<ClassYearChangeRequestShow />} />
                </Route>
                <Route path="/businesses">
                  <Route index element={<BusinessesList />} />
                  <Route path="show/:id" element={<BusinessShow />} />
                </Route>
                <Route path="/business-of-the-month" element={<BusinessOfMonthPage />} />
                <Route path="/ask-monitor" element={<AskMonitorPage />} />
                <Route path="/jaguar-owned/controls" element={<JaguarOwnedControlsPage />} />
                {/* Community manager agent */}
                <Route path="/agent/inbox" element={<CmInboxPage />} />
                <Route path="/agent/signals" element={<CmSignalsPage />} />
                <Route path="/agent/runs" element={<CmRunsPage />} />
                <Route path="/agent/controls" element={<CmControlsPage />} />
                <Route path="/text_blocklist">
                  <Route index element={<TextBlocklistList />} />
                  <Route path="create" element={<TextBlocklistCreate />} />
                  <Route path="edit/:id" element={<TextBlocklistEdit />} />
                </Route>

                <Route path="*" element={<ErrorComponent />} />
              </Route>

              <Route
                element={
                  <Authenticated key="auth-pages" fallback={<Outlet />}>
                    <NavigateToResource resource="profiles" />
                  </Authenticated>
                }
              >
                <Route path="/login" element={<Login />} />
              </Route>
            </Routes>
            <UnsavedChangesNotifier />
            {/* Brand the tab with the resolved tenant (its subdomain) instead of
                Refine's default app name — spec §11. */}
            <DocumentTitleHandler
              handler={({ autoGeneratedTitle }) =>
                brandDocumentTitle(autoGeneratedTitle, getActiveTenant())
              }
            />
          </Refine>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <ConfigProvider theme={adminTheme}>
        <AntdApp>
          <AppShell />
        </AntdApp>
      </ConfigProvider>
    </BrowserRouter>
  )
}
